import React, { useState, useEffect, useRef } from 'react';
import { initFirebase, requestToken, setupForeground, deletePushToken, showBrowserNotification, isIOS, isStandalone, isNotificationSupported } from '../utils/notifications';
import { api } from '../utils/api';

// ================================================================
//  通知タイプの定義
// ================================================================
const NOTIF_TYPES = [
  {
    id:       'kpi_reminder',
    icon:     '',
    label:    'KPI入力リマインダー',
    desc:     '当日のKPI入力を促す通知が届きます',
    adminOnly: false,
  },
  {
    id:       'improvement_alert',
    icon:     '',
    label:    '改善目標期限アラート',
    desc:     '設定した改善目標の期限が近づいたら通知が届きます',
    adminOnly: false,
  },
  {
    id:       'weekly_summary',
    icon:     '',
    label:    '週間レポート',
    desc:     '毎週の成績サマリーを通知で受け取れます',
    adminOnly: false,
  },
  {
    id:       'admin_notice',
    icon:     '',
    label:    '管理者お知らせ',
    desc:     '管理者からの重要なお知らせを受け取ります',
    adminOnly: false,
  },
  {
    id:       'unsubmitted_alert',
    icon:     '',
    label:    '未入力アラート',
    desc:     '未入力のスタッフがいる場合に通知が届きます（管理者専用）',
    adminOnly: true,
  },
];

// ================================================================
//  メインコンポーネント
// ================================================================
export default function NotificationSettings({ user }) {
  const [pushEnabled,  setPushEnabled]  = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [status,       setStatus]       = useState('');
  const [statusType,   setStatusType]   = useState('default');
  const [prefs,        setPrefs]        = useState({
    kpi_reminder: 0, improvement_alert: 0,
    weekly_summary: 0, admin_notice: 1, unsubmitted_alert: 0,
  });
  const [permission,   setPermission]   = useState('default');

  // 管理者送信フォーム
  const [sendType,    setSendType]    = useState('admin_notice');
  const [sendTitle,   setSendTitle]   = useState('');
  const [sendBody,    setSendBody]    = useState('');
  const [sendResult,  setSendResult]  = useState({ msg: '', ok: null });
  const [sending,     setSending]     = useState(false);

  const iosDevice    = isIOS();
  const standalone   = isStandalone();
  const supported    = isNotificationSupported();
  const unsubRef     = useRef(null);

  // ================================================================
  //  初期化
  // ================================================================
  useEffect(() => {
    initFirebase();
    initPage();
    return () => { if (unsubRef.current) unsubRef.current(); };
  }, []); // eslint-disable-line

  async function initPage() {
    setLoading(true);
    try {
      const perm = supported ? Notification.permission : 'unavailable';
      setPermission(perm);

      if (perm === 'granted' && localStorage.getItem('fcm_token')) {
        setPushEnabled(true);
        // フォアグラウンドハンドラを設定
        unsubRef.current = setupForeground(showBrowserNotification);
      }

      // DBから設定を取得
      const saved = await api.getNotifPrefs().catch(() => null);
      if (saved) setPrefs(p => ({ ...p, ...saved }));
    } catch {}
    setLoading(false);
  }

  // ================================================================
  //  通知を有効にする
  // ================================================================
  async function enablePush() {
    if (!supported) return;
    setSaving(true);
    setStatus('権限を要求中…'); setStatusType('loading');

    try {
      // 通知権限リクエスト
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setStatus('通知が拒否されました。ブラウザの設定から許可してください'); setStatusType('error');
        return;
      }

      // FCM トークン取得
      const token = await requestToken();
      localStorage.setItem('fcm_token', token);

      // DB に登録
      await api.registerNotifToken(token);

      // フォアグラウンドハンドラ設定
      if (unsubRef.current) unsubRef.current();
      unsubRef.current = setupForeground(showBrowserNotification);

      setPushEnabled(true);
      setStatus('プッシュ通知を有効にしました！'); setStatusType('success');
    } catch (e) {
      setStatus('エラー: '+ e.message); setStatusType('error');
    } finally {
      setSaving(false);
    }
  }

  // ================================================================
  //  通知を無効にする
  // ================================================================
  async function disablePush() {
    setSaving(true);
    try {
      const storedToken = localStorage.getItem('fcm_token');
      await deletePushToken().catch(() => {});
      await api.deleteNotifToken(storedToken).catch(() => {});
      localStorage.removeItem('fcm_token');
      if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
      setPushEnabled(false);
      setStatus('プッシュ通知を無効にしました'); setStatusType('default');
    } catch (e) {
      setStatus('エラー: '+ e.message); setStatusType('error');
    } finally {
      setSaving(false);
    }
  }

  // ================================================================
  //  通知タイプのトグル
  // ================================================================
  async function togglePref(id, checked) {
    const next = { ...prefs, [id]: checked ? 1 : 0 };
    setPrefs(next);
    try { await api.updateNotifPrefs(next); } catch {}
  }

  // ================================================================
  //  管理者：通知送信
  // ================================================================
  async function handleSend(e) {
    e.preventDefault();
    if (!sendTitle.trim()) return;
    setSending(true); setSendResult({ msg: '', ok: null });
    try {
      await api.sendNotification(sendType, sendTitle.trim(), sendBody.trim(), '/');
      setSendResult({ msg: '通知を送信しました', ok: true });
      setSendTitle(''); setSendBody('');
    } catch (e) {
      setSendResult({ msg: '送信失敗: '+ e.message, ok: false });
    } finally { setSending(false); }
  }

  // ================================================================
  //  管理者：未入力ユーザーへ通知
  // ================================================================
  async function handleUnsubmittedNotify() {
    setSending(true); setSendResult({ msg: '', ok: null });
    try {
      const res = await api.sendUnsubmittedNotify();
      setSendResult({ msg: `未入力の ${res.notified ?? 0} 名にリマインダーを送信しました`, ok: true });
    } catch (e) {
      setSendResult({ msg: '失敗: '+ e.message, ok: false });
    } finally { setSending(false); }
  }

  // ================================================================
  //  スタイルヘルパー
  // ================================================================
  const s = (...classes) => ({
    card: {
      background: 'var(--field)',
      borderRadius: 12, padding: '20px 22px', marginBottom: 16,
      boxShadow: 'none',
      border: '1px solid var(--rule-strong)',
    },
    cardTitle: {
      fontSize: 15, fontWeight: 700, color: 'var(--ink)',
      marginBottom: 16, paddingBottom: 10,
      borderBottom: '1px solid var(--rule-strong)',
    },
  });

  const STATUS_COLORS = {
    success: '#1E5540', error: '#6B3A36', loading: '#33406E', default: 'var(--rule-strong)',
  };

  // ================================================================
  //  レンダリング
  // ================================================================
  if (loading) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
        <h1 className="page-title"> 通知設定</h1>
        <div style={{ textAlign: 'center', color: 'var(--ink-mute)', padding: 40 }}>読み込み中…</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <h1 className="page-title"> 通知設定</h1>

      {/* iOS ホーム画面未追加の警告 */}
      {iosDevice && !standalone && (
        <div style={{
          background: 'var(--hold-bg)', border: '1px solid #5C4718',
          borderRadius: 10, padding: '14px 16px', marginBottom: 16,
          display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 'var(--t-note)',
        }}>
          <span style={{ fontSize: 24, flexShrink: 0 }}></span>
          <div style={{ color: 'var(--hold)', lineHeight: 1.6 }}>
            <strong>iPhone / iPad の方へ</strong><br />
            通知はホーム画面に追加したアプリ（iOS 16.4+）でのみ動作します。<br />
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--hold)' }}>
              Safari → 共有ボタン（□↑）→「ホーム画面に追加」
            </span>
          </div>
        </div>
      )}

      {/* ブラウザ非対応 */}
      {!supported && (
        <div style={{
          background: 'var(--loss-bg)', border: '1px solid #6B3A36',
          borderRadius: 10, padding: '14px 16px', marginBottom: 16, color: 'var(--loss)', fontSize: 'var(--t-note)',
        }}>
           このブラウザはプッシュ通知に対応していません（HTTPS 必須・iOS はホーム画面追加が必要）
        </div>
      )}

      {/* ===== プッシュ通知 有効/無効 ===== */}
      <div style={s().card}>
        <h2 style={s().cardTitle}> プッシュ通知</h2>

        {/* ステータス表示 */}
        {status && (
          <div style={{
            fontSize: 'var(--t-note)', fontWeight: 600, color: STATUS_COLORS[statusType] || 'var(--ink-soft)',
            background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 14,
          }}>
            {status}
          </div>
        )}

        {/* 権限が denied */}
        {permission === 'denied' && (
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--loss)', marginBottom: 14 }}>
             通知が拒否されています。ブラウザのサイト設定から通知を「許可」にしてください。
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: pushEnabled ? 'var(--gain)' : 'var(--ink-soft)' }}>
              {pushEnabled ? '通知 有効': '通知 無効'}
            </div>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginTop: 4 }}>
              {pushEnabled
                ? 'アプリを閉じていても通知が届きます'
                : '「有効にする」を押して通知を許可してください'}
            </div>
          </div>
          {pushEnabled ? (
            <button
              onClick={disablePush}
              disabled={saving}
              style={{
                padding: '10px 20px', borderRadius: 8,
                border: '1.5px solid #6B3A36',
                background: 'var(--loss-bg)', color: 'var(--loss)',
                fontSize: 'var(--t-note)', fontWeight: 600, cursor: 'pointer',
              }}
            >
               無効にする
            </button>
          ) : (
            <button
              onClick={enablePush}
              disabled={saving || !supported || permission === 'denied'}
              style={{
                padding: '10px 20px', borderRadius: 8, border: 'none',
                background: saving ? 'var(--field)' : 'var(--mark-soft)',
                color: '#fff', fontSize: 'var(--t-note)', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: 'none',
              }}
            >
              {saving ? '⏳ 設定中…': '有効にする'}
            </button>
          )}
        </div>
      </div>

      {/* ===== 通知の種類 ===== */}
      {pushEnabled && (
        <div style={s().card}>
          <h2 style={s().cardTitle}> 受け取る通知の種類</h2>
          <p style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginBottom: 16 }}>
            ON にした通知のみ届きます。変更は即時保存されます。
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {NOTIF_TYPES
              .filter(t => !t.adminOnly || user.role === 'admin')
              .map(t => (
                <ToggleRow
                  key={t.id}
                  icon={t.icon}
                  label={t.label}
                  desc={t.desc}
                  checked={prefs[t.id] === 1}
                  onChange={v => togglePref(t.id, v)}
                  adminOnly={t.adminOnly}
                />
              ))}
          </div>
        </div>
      )}

      {/* ===== 管理者：通知送信 ===== */}
      {user.role === 'admin' && (
        <div style={s().card}>
          <h2 style={s().cardTitle}> 通知を送信する（管理者）</h2>

          {/* 未入力チェックボタン */}
          <div style={{
            background: 'var(--mark-soft)', border: '1px solid #33406E',
            borderRadius: 10, padding: '14px 16px', marginBottom: 16,
          }}>
            <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--mark)', marginBottom: 6 }}>
               本日 KPI 未入力のスタッフに通知
            </div>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginBottom: 12 }}>
              「KPI入力リマインダー」を有効にしているスタッフのうち、今日まだ入力していない人にリマインダーを送ります。
            </div>
            <button
              onClick={handleUnsubmittedNotify}
              disabled={sending}
              style={{
                padding: '9px 18px', borderRadius: 8, border: 'none',
                background: 'var(--mark-soft)',
                color: '#fff', fontSize: 'var(--t-note)', fontWeight: 700, cursor: 'pointer',
              }}
            >
              {sending ? '⏳ 送信中…': '未入力スタッフにリマインダー送信'}
            </button>
          </div>

          {/* 任意通知送信フォーム */}
          <form onSubmit={handleSend} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>
              任意の通知を送信
            </div>

            {/* 通知タイプ選択 */}
            <div>
              <label style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-soft)', display: 'block', marginBottom: 5 }}>
                送信先（購読者を絞り込む通知タイプ）
              </label>
              <select
                value={sendType}
                onChange={e => setSendType(e.target.value)}
                className="form-input"
                style={{ width: '100%' }}
              >
                {NOTIF_TYPES.map(t => (
                  <option key={t.id} value={t.id}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>

            {/* タイトル */}
            <div>
              <label style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-soft)', display: 'block', marginBottom: 5 }}>
                タイトル <span style={{ color: 'var(--loss)' }}>*</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={sendTitle}
                onChange={e => setSendTitle(e.target.value)}
                placeholder="例：明日は早朝ブースの開始時間が変更です"
                required
                style={{ width: '100%' }}
              />
            </div>

            {/* 本文 */}
            <div>
              <label style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-soft)', display: 'block', marginBottom: 5 }}>
                本文
              </label>
              <textarea
                className="form-input"
                value={sendBody}
                onChange={e => setSendBody(e.target.value)}
                placeholder="通知の詳細内容（省略可）"
                style={{ height: 72, resize: 'vertical', width: '100%' }}
              />
            </div>

            <button
              type="submit"
              disabled={sending || !sendTitle.trim()}
              style={{
                padding: '11px 20px', borderRadius: 8, border: 'none',
                background: sending || !sendTitle.trim()
                  ? 'var(--field)'
                  : 'var(--mark-soft)',
                color: '#fff', fontSize: 14, fontWeight: 700,
                cursor: sending || !sendTitle.trim() ? 'not-allowed' : 'pointer',
                boxShadow: 'none',
              }}
            >
              {sending ? '⏳ 送信中…': '送信する'}
            </button>
          </form>

          {/* 送信結果 */}
          {sendResult.msg && (
            <div style={{
              marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 'var(--t-note)', fontWeight: 600,
              background: sendResult.ok ? 'var(--gain-bg)' : 'var(--loss-bg)',
              color: sendResult.ok ? 'var(--gain)' : 'var(--loss)',
            }}>
              {sendResult.msg}
            </div>
          )}
        </div>
      )}

      {/* ===== 設定上の注意 ===== */}
      <div style={{
        background: 'var(--mark-soft)', border: '1px solid #33406E',
        borderRadius: 10, padding: '14px 16px', fontSize: 'var(--t-note)', color: 'var(--ink-mute)', lineHeight: 1.7,
      }}>
        <strong style={{ color: 'var(--mark)'}}> 通知機能を使うには</strong><br />
        ① Firebase プロジェクトを作成<br />
        ② <code style={{ background: 'var(--mark-soft)', padding: '1px 5px', borderRadius: 3, color: 'var(--mark)' }}>public/firebase-sw-config.js</code> に設定値を入力<br />
        ③ Cloudflare Worker に <code style={{ background: 'var(--mark-soft)', padding: '1px 5px', borderRadius: 3, color: 'var(--mark)' }}>FCM_SERVICE_ACCOUNT</code> シークレットを設定<br />
        詳細は README.md を参照してください。
      </div>
    </div>
  );
}

// ================================================================
//  トグルスイッチ行
// ================================================================
function ToggleRow({ icon, label, desc, checked, onChange, adminOnly }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 14px', borderRadius: 10,
      background: 'var(--field)',
      border: '1px solid var(--rule-strong)',
      transition: 'background 0.15s',
    }}>
      <span style={{ fontSize: 22, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{label}</span>
          {adminOnly && (
            <span style={{
              fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--hold)',
              background: 'var(--hold-bg)', padding: '1px 6px', borderRadius: 4,
            }}>管理者</span>
          )}
        </div>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginTop: 2 }}>{desc}</div>
      </div>
      {/* トグルスイッチ */}
      <button
        type="button"
        onClick={() => onChange(!checked)}
        style={{
          width: 48, height: 26, borderRadius: 13, border: 'none',
          background: checked ? 'var(--mark-soft)' : 'var(--field)',
          cursor: 'pointer', position: 'relative', flexShrink: 0,
          transition: 'background 0.2s',
          boxShadow: checked ? '0 2px 8px transparent' : 'none',
        }}
        aria-label={`${label}を${checked ? 'OFF' : 'ON'}にする`}
      >
        <div style={{
          width: 20, height: 20, borderRadius: '50%', background: '#fff',
          position: 'absolute', top: 3,
          left: checked ? 25 : 3,
          transition: 'left 0.2s',
          boxShadow: 'none',
        }} />
      </button>
    </div>
  );
}
